import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import AdminNftPictures from "@/components/AdminNftPictures";
import AdminNftArt from "@/components/AdminNftArt";
import AdminChampions from "@/components/AdminChampions";
import AdminGifts from "@/components/AdminGifts";
import AdminPups from "@/components/AdminPups";
import AdminUniqueLooks from "@/components/AdminUniqueLooks";
import AdminSolana from "@/components/AdminSolana";
import AdminWelcome from "@/components/AdminWelcome";
import AdminTopUp from "@/components/AdminTopUp";
import AdminRefreshWallets from "@/components/AdminRefreshWallets";
import AdminSalesSwitches from "@/components/AdminSalesSwitches";

const BOOST_LABELS = { c1_atk: "Collection 1 · Attack %", c2_hpdef: "Collection 2 · Health & Defense %", c3_xploot: "Collection 3 · XP & loot %", pack_bonus: "Pack Bonus · all stats %" };

function Players() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const load = () => api.get("/admin/players").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const shown = rows.filter((r) => !q || [r.email, r.name, r.wolf?.name].some((x) => x?.toLowerCase().includes(q.toLowerCase())));
  return (
    <div className="space-y-4">
    <Input data-testid="admin-players-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by email, name or wolf" className="bg-slate-900 max-w-sm" />
    <div className="glass rounded-2xl overflow-x-auto">
      <Table data-testid="admin-players-table">
        <TableHeader><TableRow><TableHead>Player</TableHead><TableHead>Wolf</TableHead><TableHead>Level</TableHead><TableHead>Progress</TableHead><TableHead>Credits</TableHead><TableHead>NFTs</TableHead><TableHead /></TableRow></TableHeader>
        <TableBody>
          {shown.map((r) => (
            <TableRow key={r.user_id} data-testid={`admin-player-${r.user_id}`}>
              <TableCell><p className="text-slate-100">{r.name}</p><p className="text-xs text-slate-500">{r.email}</p></TableCell>
              <TableCell>{r.wolf?.name || "—"}</TableCell>
              <TableCell className="font-mono-game">{r.wolf?.level ?? "—"}</TableCell>
              <TableCell className="text-xs">{r.wolf ? `Region ${r.wolf.region_idx || 1} · ${r.wolf.highest_cleared || 0} cleared` : "—"}</TableCell>
              <TableCell className="font-mono-game text-amber-200">{r.credits}</TableCell>
              <TableCell className="text-xs">
                {r.wallet ? <><span className="font-mono-game text-slate-400">{r.wallet.slice(0, 6)}…{r.wallet.slice(-4)}</span>
                  <span className="ml-2 text-amber-300">{["c1", "c2", "c3"].filter((k) => r.holdings?.[k]).map((k) => k.toUpperCase()).join(" ") || "none"}</span></> : "—"}
              </TableCell>
              <TableCell><AdminTopUp player={r} onDone={load} /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
    </div>
  );
}

function Sales() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/sales").then(({ data }) => setD(data)); }, []);
  if (!d) return null;
  return (
    <div className="space-y-4">
      <AdminSalesSwitches />
      <div className="grid grid-cols-3 gap-4">
        {[["Revenue", `$${d.total_revenue.toFixed(2)}`, "admin-total-revenue"], ["Paid orders", d.paid_count, "admin-paid-count"], ["Credits sold", d.total_credits, "admin-credits-sold"]].map(([l, v, t]) => (
          <div key={l} className="glass rounded-2xl p-5"><p className="text-xs text-slate-400">{l}</p><p data-testid={t} className="font-mono-game text-2xl text-white mt-1">{v}</p></div>
        ))}
      </div>
      <div className="glass rounded-2xl overflow-x-auto">
        <Table data-testid="admin-sales-table">
          <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Player</TableHead><TableHead>Pack</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
          <TableBody>
            {d.transactions.map((t) => (
              <TableRow key={t.session_id}>
                <TableCell className="text-xs">{new Date(t.created_at).toLocaleString()}</TableCell>
                <TableCell className="text-xs">{t.email}</TableCell>
                <TableCell>{t.credits} credits</TableCell>
                <TableCell className="font-mono-game">${t.amount.toFixed(2)}</TableCell>
                <TableCell><span className={`text-xs px-2 py-0.5 rounded-full ${t.payment_status === "paid" ? "bg-emerald-900/60 text-emerald-200" : "bg-slate-800 text-slate-400"}`}>{t.payment_status}</span></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function Settings() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/settings").then(({ data }) => setS(data)); }, []);
  if (!s) return null;
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/admin/settings", { boosts: s.boosts, packs: s.packs, collections: s.collections }); setS(data); toast.success("Settings saved"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const num = (v) => parseInt(v || "0", 10);
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="glass rounded-2xl p-6 space-y-4">
        <h3 className="font-display text-xl text-amber-100">Boost percentages</h3>
        {Object.entries(BOOST_LABELS).map(([k, l]) => (
          <label key={k} className="flex items-center justify-between gap-4 text-sm">{l}
            <Input data-testid={`admin-boost-${k}`} type="number" className="w-24 bg-slate-900" value={s.boosts[k]} onChange={(e) => setS({ ...s, boosts: { ...s.boosts, [k]: num(e.target.value) } })} />
          </label>
        ))}
        <h3 className="font-display text-xl text-amber-100 pt-4">Collections</h3>
        {s.collections.map((c, i) => (
          <div key={c.key} className="space-y-2">
            <Input data-testid={`admin-collection-name-${c.key}`} className="bg-slate-900" value={c.name} onChange={(e) => { const cs = [...s.collections]; cs[i] = { ...c, name: e.target.value }; setS({ ...s, collections: cs }); }} />
            <Input data-testid={`admin-collection-address-${c.key}`} className="bg-slate-900 font-mono-game text-xs" value={c.address} onChange={(e) => { const cs = [...s.collections]; cs[i] = { ...c, address: e.target.value.trim() }; setS({ ...s, collections: cs }); }} />
          </div>
        ))}
      </div>
      <div className="glass rounded-2xl p-6 space-y-4">
        <h3 className="font-display text-xl text-amber-100">Credit packs</h3>
        {Object.entries(s.packs).map(([k, p]) => (
          <div key={k} className="grid grid-cols-3 gap-2 items-center">
            <Input data-testid={`admin-pack-name-${k}`} className="bg-slate-900" value={p.name} onChange={(e) => setS({ ...s, packs: { ...s.packs, [k]: { ...p, name: e.target.value } } })} />
            <Input data-testid={`admin-pack-credits-${k}`} type="number" className="bg-slate-900" value={p.credits} onChange={(e) => setS({ ...s, packs: { ...s.packs, [k]: { ...p, credits: num(e.target.value) } } })} />
            <Input data-testid={`admin-pack-price-${k}`} type="number" step="0.01" className="bg-slate-900" value={(p.price_cents / 100).toFixed(2)}
              onChange={(e) => setS({ ...s, packs: { ...s.packs, [k]: { ...p, price_cents: Math.round(parseFloat(e.target.value || "0") * 100) } } })} />
          </div>
        ))}
        <p className="text-xs text-slate-500">Name · credits · price (USD). Price changes update Stripe automatically.</p>
        <button data-testid="admin-save-settings-btn" disabled={busy} onClick={save} className="ember-btn w-full py-3 rounded-lg text-sm mt-4">{busy ? "SAVING…" : "SAVE SETTINGS"}</button>
      </div>
    </div>
  );
}

export default function Admin() {
  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="font-display text-4xl text-amber-100 mb-8">Pack Council</h1>
      <Tabs defaultValue="players">
        <TabsList className="bg-slate-900 mb-6 flex-wrap h-auto">
          <TabsTrigger value="players" data-testid="admin-tab-players">Players</TabsTrigger>
          <TabsTrigger value="sales" data-testid="admin-tab-sales">Sales</TabsTrigger>
          <TabsTrigger value="settings" data-testid="admin-tab-settings">Settings</TabsTrigger>
          <TabsTrigger value="art" data-testid="admin-tab-art">NFT Art</TabsTrigger>
          <TabsTrigger value="pictures" data-testid="admin-tab-pictures">NFT Pictures</TabsTrigger>
          <TabsTrigger value="champions" data-testid="admin-tab-champions">Champions</TabsTrigger>
          <TabsTrigger value="gifts" data-testid="admin-tab-gifts">Gifts</TabsTrigger>
          <TabsTrigger value="pups" data-testid="admin-tab-pups">Pup orders</TabsTrigger>
          <TabsTrigger value="looks" data-testid="admin-tab-looks">1/1 Looks</TabsTrigger>
          <TabsTrigger value="solana" data-testid="admin-tab-solana">Solana</TabsTrigger>
        </TabsList>
        <TabsContent value="players"><AdminRefreshWallets /><AdminWelcome /><Players /></TabsContent>
        <TabsContent value="sales"><Sales /></TabsContent>
        <TabsContent value="settings"><Settings /></TabsContent>
        <TabsContent value="art"><AdminNftArt /></TabsContent>
        <TabsContent value="pictures"><AdminNftPictures /></TabsContent>
        <TabsContent value="champions"><AdminChampions /></TabsContent>
        <TabsContent value="gifts"><AdminGifts /></TabsContent>
        <TabsContent value="pups"><AdminPups /></TabsContent>
        <TabsContent value="looks"><AdminUniqueLooks /></TabsContent>
        <TabsContent value="solana"><AdminSolana /></TabsContent>
      </Tabs>
    </main>
  );
}
